import { useRouter } from "vue-router"
import { useAuthStore } from "@/store/auth.store"
import { storeToRefs } from "pinia"
import { inject, watch } from "vue"
import { AuthController } from "@/controllers/auth-controller"
import { getToken, useAuth, useClerk, useUser } from "@clerk/vue"
import { setUser } from "@sentry/vue"
import { isProductionEnvironment } from "@/helper/app/browser-helper"
import { useDialogStore } from "@/store/dialog.store"
import { DialogTypes } from "@/interfaces/component/dialog.interface"
import { EMAIL_LINK_ROUTES } from "@/router/auth-paths"

// Add helper function to extract domain
function getDomainFromApi() {
  const apiUrl: string = import.meta.env.VITE_API_URL
  // A relative API URL (e.g. "/d" on Coolify preview deployments, where the
  // frontend and Django backend share one host with the backend under /d)
  // has no domain to widen the cookie to -- a host-only cookie (empty domain)
  // is exactly right, since both are on the same host. Only absolute URLs
  // (prod: app.peanuts.nl / api.peanuts.nl) need a shared parent domain.
  if (!apiUrl || apiUrl.startsWith("/")) return ""
  try {
    const url = new URL(apiUrl)
    // Extract the base domain (e.g., from api.peanuts.nl get peanuts.nl)
    const parts = url.hostname.split(".")
    return parts.length > 2 ? `.${parts.slice(-2).join(".")}` : `.${url.hostname}`
  } catch (e) {
    console.error("Failed to parse API URL:", e)
    return ""
  }
}

// Add function to set JWT cookie
async function setJWTCookie() {
  const token = await getToken({ template: "jwt" })
  if (token) {
    const secure = isProductionEnvironment() ? "Secure;" : ""
    const domain = getDomainFromApi()
    document.cookie = `clerk_jwt=${token}; path=/; ${secure} domain=${domain}; SameSite=Lax; Secure; max-age=3600`
  }
}

export { setJWTCookie }

export default function useAuthentication(
  authController: AuthController = inject("authController")!
) {
  const clerk = useClerk()
  const router = useRouter()
  const authStore = useAuthStore()
  const { isLoaded, signOut, userId, sessionId } = useAuth()
  const { activeUser } = storeToRefs(authStore)
  const { user } = useUser()

  setJWTCookie()

  clerk.value?.addListener((resources) => {
    setJWTCookie()
    // Watch for Clerk user changes and update Sentry user accordingly
    if (resources.user) {
      setUser({
        id: resources.user.id,
        username:
          resources.user.phoneNumbers[0]?.phoneNumber ||
          resources.user.primaryEmailAddress?.emailAddress
      })
    } else {
      setUser(null)
    }
  })

  let authSyncPromise: Promise<void> | null = null

  async function syncAuthenticatedState() {
    if (!isLoaded.value) return
    if (authSyncPromise) return authSyncPromise

    authSyncPromise = (async () => {
      await setUserIfAuthenticated()
      await setJWTCookie()
    })()

    try {
      return await authSyncPromise
    } finally {
      authSyncPromise = null
    }
  }

  async function initialize(): Promise<void> {
    // Sign-in can complete without a full reload, so re-sync when Clerk auth state changes.
    // TODO: this watcher will be duplicated for every authentication import!!!
    // Await the first sync so App.vue's loading screen (gated on authLoaded) stays up
    // until auth/current-user/ has finished. The watcher keeps running for later sign-ins.
    let initialSyncDone = false
    await new Promise<void>((resolve) => {
      watch(
        [isLoaded, userId, sessionId],
        async ([loaded]) => {
          if (!loaded) return
          await syncAuthenticatedState()
          if (!initialSyncDone) {
            initialSyncDone = true
            resolve()
          }
        },
        { immediate: true }
      )
    })
  }

  function loginRedirect() {
    if (clerk.value?.loaded) {
      clerk.value.redirectToSignIn()
    }
  }

  async function logoutRedirect() {
    await signOut.value()
    router.replace({ name: "login" })
    activeUser.value = null
  }

  /**
   * Try to accept an invite and set the user if successful.
   */
  async function tryAcceptInvite(inviteCode: string) {
    try {
      // Stay on /invite while the backend turns the Clerk session into a Django user.
      await router.replace({ name: "invite", query: { "is-accepting": "true" } })
      const user = await authController.acceptInvite(inviteCode)
      if (user) {
        // Re-run auth sync now that invite acceptance may have created the Django user.
        await setUserIfAuthenticated()
      }
    } catch (e: any) {
      console.error(e)
      // Show error modal
      showLoginErrorDialog()
    }
  }

  async function setUserIfAuthenticated() {
    // The tab opened from the e-mail login link only verifies the link. It must not
    // fetch the Django user or redirect to "/", which would boot the planning view there.
    const currentRouteName = router.currentRoute.value.name?.toString() || ""
    if (EMAIL_LINK_ROUTES.includes(currentRouteName)) {
      return
    }

    if (user.value) {
      console.log("User found, checking if we can swap entrance user for django user...")
      // Check if we can swap entra user for django user
      const djangoUser = await authController.getDjangoUser()
      if (djangoUser) {
        activeUser.value = djangoUser
        // redirect user
        const routeName = router.currentRoute.value!.name!.toString()
        const doNotRedirectForRouteNames = [
          "socket-tester",
          "invite",
          "templates",
          "work-period-template-form",
          "instruction-activity-template-form"
        ]
        const isSpecialRoute =
          routeName.startsWith("klantportaal") ||
          routeName.startsWith("dashboard") ||
          routeName.startsWith("customer")

        if (!doNotRedirectForRouteNames.includes(routeName) && !isSpecialRoute) {
          // DON'T USE NAMED ROUTES HERE
          await router.replace("/")
        }
      } else {
        console.log("No Django user found, checking if we can accept invite...")
        // unsafeMetadata: Client-readable/writable metadata; untrusted because users can modify it.
        if (user.value?.unsafeMetadata.invitationCode) {
          console.log("Invitation code found, trying to accept invite...")
          await tryAcceptInvite(user.value?.unsafeMetadata.invitationCode as string)
          if (activeUser.value) {
            console.log("Django user found after invite acceptance, redirecting to home page...")
            // If invite acceptance produced an active Django user, continue into the app.
            await router.replace("/")
            return
          }

          console.log("No Django user after invite acceptance means the flow is still incomplete.")
          // No Django user after invite acceptance means the flow is still incomplete.
          showLoginErrorDialog()
        } else {
          console.log("No invitation code found, showing login error dialog...")
          showLoginErrorDialog()
        }
      }
    }

    // If there are no accounts, redirect to login page
    else {
      const routeName = router.currentRoute.value.name?.toString() || ""
      const excludedPaths = ["error", "invite", "login"] // Some paths should be accessible without authentication.
      const isCustomerRoute = routeName.startsWith("customer")
      if (!excludedPaths.includes(routeName) && !isCustomerRoute) {
        await router.replace({ name: "login" })
      }
    }
  }

  function showLoginErrorDialog() {
    const dialogStore = useDialogStore()
    dialogStore.currentDialog = {
      title: "Het is niet gelukt om je in te loggen",
      body: "Probeer het nog eens of neem contact op met de planner.",
      type: DialogTypes.OK,
      lock: true, // disable closing the dialog by clicking outside of it
      onConfirm: async () => {
        // Logout user and redirect to login page
        await signOut.value()
        await router.replace({ name: "login" })
        // Clear dialog
        dialogStore.currentDialog = null
      }
    }
  }

  async function reloadClerkClient() {
    // After an email confirmation link is clicked, Clerk may have created a session
    // server-side, but the local Clerk client state is stale (user, session, and
    // client.sessions are all still from before the link was clicked).
    // Force the client to re-fetch its state from the Clerk Frontend API, then
    // activate the session so the reactive user/session refs get hydrated.
    if (clerk.value?.client) {
      await clerk.value.client.reload()
      const activeSession =
        clerk.value.client.sessions.find((s) => s.status === "active") ??
        clerk.value.client.sessions.find((s) => s.id === clerk.value!.client?.lastActiveSessionId)
      if (activeSession) {
        // // Close any open Clerk modal (sign-in / sign-up) before activating the
        // // session. If the modal stays open its full-screen overlay keeps the page
        // // in a disabled / non-interactive state even after the user is logged in.
        clerk.value.closeSignIn?.()
        clerk.value.closeSignUp?.()

        await clerk.value.setActive({ session: activeSession.id })
      }
    }
  }

  return {
    initialize,
    loginRedirect,
    logoutRedirect,
    reloadClerkClient
  }
}
